import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Pencil, Trash2, ListChecks, Users, RotateCcw, AlertTriangle } from 'lucide-react';
import { IconButton } from '@anyemp/ui-kit';
import toast from 'react-hot-toast';
import StageForm from './StageForm';
import ConfirmDialog from '../UI/ConfirmDialog';
import { getTalentTypeLabel, getTalentTypeColor } from '../../utils/talentTypeUtils';

const StageCard = ({
  stage,
  onUpdate,
  onDelete,
  onRestore,
  onManageStatuses,
  isEditing = false,
  onEdit,
  onCancelEdit
}) => {
  const navigate = useNavigate();
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  // Число талантов, из-за которых удалить нельзя; null — модалка закрыта
  const [blockingCount, setBlockingCount] = useState(null);

  const talentsCount = stage.talents_count ?? 0;
  // is_active = false — стадия удалена (мягкое удаление), отдельного флага нет
  const isDeleted = !stage.is_active;

  // Стадию с талантами удалить нельзя: вместо подтверждения сразу объясняем,
  // что сначала нужно перевести или удалить таланты
  const handleDelete = () => {
    if (talentsCount > 0) {
      setBlockingCount(talentsCount);
    } else {
      setDeleteConfirm(true);
    }
  };

  const confirmDelete = async () => {
    setIsDeleting(true);
    try {
      await onDelete(stage.id);
    } catch (error) {
      // Счётчик на карточке мог устареть: пока окно было открыто, на стадию
      // кого-то перевели. Бэкенд вернёт 409 с актуальным числом
      const count = error?.response?.data?.talents_count;
      if (error?.response?.status === 409 && count) {
        setBlockingCount(count);
      } else {
        console.error('Error deleting stage:', error);
        toast.error(error?.response?.data?.error || error.message || 'Error deleting stage');
      }
    } finally {
      setIsDeleting(false);
      setDeleteConfirm(false);
    }
  };

  const handleRestore = async () => {
    try {
      await onRestore(stage.id);
    } catch (error) {
      console.error('Error restoring stage:', error);
      toast.error(error?.response?.data?.error || error.message || 'Error restoring stage');
    }
  };

  const handleUpdate = async (updateData) => {
    try {
      await onUpdate(stage.id, updateData);
    } catch (error) {
      console.error('Error updating stage:', error);
      toast.error('Error updating stage: ' + error.message);
    }
  };

  if (isEditing) {
    return (
      <div className="bg-light-bg-primary dark:bg-dark-bg-secondary rounded-lg shadow-md border border-light-border-primary dark:border-dark-border-primary p-6">
        <StageForm
          initialData={stage}
          onSubmit={handleUpdate}
          onCancel={onCancelEdit}
          submitLabel="Save"
        />
      </div>
    );
  }

  // Один плотный ряд вместо стопки блоков: цвет/имя/ключ/описание — в одну
  // строку с truncate, статус и order — короткой меткой сбоку, иконки действий
  // уменьшены. Список стадий редко превышает 7-10 строк, но должен читаться
  // целиком без скролла.
  return (
    <div className={`flex min-w-0 max-w-full flex-col items-stretch gap-2 rounded-lg border border-light-border-primary bg-light-bg-primary px-3 py-2 shadow-sm transition-shadow duration-200 hover:shadow-md dark:border-dark-border-primary dark:bg-dark-bg-secondary sm:flex-row sm:items-center sm:gap-3 ${isDeleted ? 'opacity-60' : ''}`}>
      <div className="flex min-w-0 max-w-full items-center gap-2 sm:contents">
        <div
          className="h-3 w-3 flex-shrink-0 rounded-full border border-gray-300"
          style={{ backgroundColor: stage.color || '#6B7280' }}
        />

        <h3 className="min-w-0 max-w-full flex-1 truncate text-sm font-semibold text-light-text-primary dark:text-dark-text-primary sm:max-w-[10rem] sm:flex-none sm:flex-shrink-0" title={stage.name}>
          {stage.name}
        </h3>

        <span className="hidden sm:inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-mono bg-light-bg-tertiary dark:bg-dark-bg-tertiary text-light-text-tertiary dark:text-dark-text-tertiary flex-shrink-0">
          {stage.key}
        </span>

        {stage.talent_type && (
          <span
            className={`inline-flex max-w-[45%] flex-shrink-0 items-center truncate rounded px-1.5 py-0.5 text-[11px] font-medium sm:max-w-none ${getTalentTypeColor(stage.talent_type)}`}
            title="Talents on this stage have this type"
          >
            {getTalentTypeLabel(stage.talent_type)}
          </span>
        )}
      </div>

      {stage.description && (
        <p
          className="hidden md:block flex-1 min-w-0 truncate text-xs text-light-text-secondary dark:text-dark-text-secondary"
          title={stage.description}
        >
          {stage.description}
        </p>
      )}

      <div className="flex-1 md:flex-none" />

      <div className="flex min-w-0 max-w-full flex-wrap items-center justify-between gap-2 sm:contents sm:flex-nowrap">
        {!isDeleted && (
          <span
            className="inline-flex items-center gap-1 text-xs text-light-text-secondary dark:text-dark-text-secondary flex-shrink-0"
            title={`${talentsCount} talent(s) on this stage`}
          >
            <Users className="w-3.5 h-3.5" />
            {talentsCount}
          </span>
        )}

        {isDeleted && (
          <span className="text-xs font-medium flex-shrink-0 text-light-text-tertiary dark:text-dark-text-tertiary">
            Deleted
          </span>
        )}
        <span className="hidden sm:inline text-xs text-light-text-tertiary dark:text-dark-text-tertiary flex-shrink-0">
          #{stage.stage_order}
        </span>

        <div className="flex flex-shrink-0 flex-wrap items-center justify-end gap-0.5 sm:flex-nowrap">
          {isDeleted ? (
            <IconButton
              variant="ghost"
              color="primary"
              size="small"
              aria-label="Restore"
              title="Restore"
              onClick={handleRestore}
              icon={<RotateCcw className="w-4 h-4" />}
            />
          ) : (
            <>
              <IconButton
                variant="ghost"
                color="tertiary"
                size="small"
                aria-label="Manage statuses"
                title="Manage statuses"
                onClick={() => onManageStatuses(stage)}
                icon={<ListChecks className="w-4 h-4" />}
              />
              <IconButton
                variant="ghost"
                color="info"
                size="small"
                aria-label="Edit"
                title="Edit"
                onClick={() => onEdit(stage.id)}
                icon={<Pencil className="w-4 h-4" />}
              />
              <IconButton
                variant="ghost"
                color="error"
                size="small"
                aria-label="Delete"
                title="Delete"
                disabled={isDeleting}
                onClick={handleDelete}
                icon={<Trash2 className="w-4 h-4" />}
              />
            </>
          )}
        </div>
      </div>

      <ConfirmDialog
        isOpen={deleteConfirm}
        onClose={() => setDeleteConfirm(false)}
        onConfirm={confirmDelete}
        confirmStyle="danger"
        title="Delete Stage"
        message={`Delete "${stage.name}"? It will disappear from navigation and stage pickers. You can restore it later via "Show deleted".`}
        confirmText="Delete"
        isLoading={isDeleting}
      />

      <ConfirmDialog
        isOpen={blockingCount !== null}
        onClose={() => setBlockingCount(null)}
        onConfirm={() => navigate(`/talents?stage_id=${stage.id}`)}
        icon={AlertTriangle}
        iconColor="text-amber-500 dark:text-amber-400"
        title="Stage can't be deleted yet"
        message={
          <>
            <strong>{blockingCount}</strong> talent(s) are on the stage <strong>"{stage.name}"</strong>.
            {' '}Move them to another stage or delete them first, then delete the stage.
          </>
        }
        confirmText="View talents"
        cancelText="Close"
      />
    </div>
  );
};

export default StageCard;
